import Vue from "vue";
import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import { resolvePermissionCheck } from "@/utils/groupPermissions";

const state = {
    orders: [],
    orders_loading: false,
    orders_loading_errors: null,

    orderitem: null,
    orderitem_saving: false,
    orderitem_saving_errors: {},

    orderitem_deleting: false,
    orderitem_deleting_errors: null,
};

function permissionDeniedError() {
    const message = Vue.prototype.$translate
        ? Vue.prototype.$translate("errors.permission_denied")
        : "Permission denied";
    return {
        success: false,
        code: 403,
        message,
        details: { non_field_errors: [message] },
    };
}

const mutations = {
    updateDeliverySites(state, deliverysites) {
        state.deliverysites = deliverysites;
    },
    updateDeliverySitesLoading(state, loading) {
        state.deliverysites_loading = loading;
    },
    updateDeliverySitesLoadingErrors(state, errors) {
        state.deliverysites_loading_errors = errors;
    },
    updateOrderItemDeleting(state, deleting) {
        state.orderitem_deleting = deleting;
    },
    updateOrderItemDeletingErrors(state, errors) {
        state.orderitem_deleting_errors = errors;
    },
    updateOrders(state, orders) {
        state.orders = orders;
    },
    updateOrdersLoading(state, loading) {
        state.orders_loading = loading;
    },
    updateOrdersLoadingErrors(state, errors) {
        state.orders_loading_errors = errors;
    },

    updateOrderItemSaving(state, saving) {
        state.orderitem_saving = saving;
    },
    updateOrderItemSavingErrors(state, errors) {
        state.orderitem_saving_errors = errors;
    },

    updateOrderItem(state, orderitem) {
        state.orderitem = orderitem;
    },
};

const actions = {


    async fetchOrders({ commit, rootGetters, state }, params) {
        if (
            rootGetters["session/current_user_permissions"].indexOf(
                "order.view_order"
            ) === -1
        ) {
            return;
        }

        commit("updateOrdersLoading", true);
        commit("updateOrdersLoadingErrors", null);

        try {
            const response = await axios.get("/api/customerorders/", {
                params: { ready: false, limit: 20 },
            });
            commit("updateOrders", response.data.results);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateOrdersLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateOrdersLoading", false);
        }
    },

    async saveOrderItem({ commit, rootGetters, state }, params) {
        const permissions = rootGetters["session/current_user_permissions"];
        const hasAddPermission = resolvePermissionCheck(
            permissions,
            "order.add_orderitem",
        );
        const hasChangePermission = resolvePermissionCheck(
            permissions,
            "order.change_orderitem",
        );

        if (!state.orderitem.id && !hasAddPermission) {
            const error = permissionDeniedError();
            commit("updateOrderItemSavingErrors", error.details);
            throw error;
        }
        if (state.orderitem.id && !hasChangePermission) {
            const error = permissionDeniedError();
            commit("updateOrderItemSavingErrors", error.details);
            throw error;
        }

        commit("updateOrderItemSaving", true);
        commit("updateOrderItemSavingErrors", {});

        let url = "/api/customerorderitems/";
        let method = axios.post;

        if (state.orderitem.id) {
            url = `/api/customerorderitems/${state.orderitem.id}/`;
            method = axios.put;
        }
        try {
            const response = await method(url, state.orderitem);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateOrderItemSavingErrors", error.details);
            throw error;
        } finally {
            commit("updateOrderItemSaving", false);
        }
    },

    async deleteOrderItem({ commit, dispatch, state }, params) {
        commit("updateOrderItemDeleting", true);
        commit("updateOrderItemDeletingErrors", null);

        const url = `/api/customerorderitems/${params.instance.id}/`;

        try {
            const response = await axios.delete(url);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit("updateOrderItemDeletingErrors", error.details);
            throw error;
        } finally {
            commit("updateOrderItemDeleting", false);
        }
    },

    async init({ commit, dispatch, state }, params) {

        await commit("updateOrderItem", params.orderitem);
        await commit("updateOrderItemSavingErrors", {});
        dispatch("fetchOrders");
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
